import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Check, Users, ChevronDown } from "lucide-react";

/** Multi-crew filter with text search + checkbox dropdown.
 *
 * Props:
 *   crews          — array of {id, name, email, role}
 *   selectedIds    — Set<string> of selected crew ids (empty Set = "show all")
 *   onChange(next) — callback with new Set<string>
 *
 * Behaviour:
 *   - Empty Set means "no explicit selection — show all crews"
 *   - Any non-empty Set acts as an allow-list
 *   - Text search filters the dropdown list AND (when present) the calendar via selectedIds
 *   - "Select all" / "Clear" shortcuts inside the dropdown
 */
export default function CrewFilter({ crews, selectedIds, onChange }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wrapperRef = useRef(null);

    // Dropdown auto-close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        document.addEventListener("touchstart", handler, { passive: true });
        return () => {
            document.removeEventListener("mousedown", handler);
            document.removeEventListener("touchstart", handler);
        };
    }, [open]);

    const visibleCrews = useMemo(() => {
        const needle = search.trim().toLowerCase();
        if (!needle) return crews;
        return crews.filter((c) =>
            (c.name || c.email || "").toLowerCase().includes(needle),
        );
    }, [crews, search]);

    const toggle = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        onChange(next);
    };

    const selectAll = () => onChange(new Set(crews.map((c) => c.id)));
    const clearAll = () => onChange(new Set());

    const summary = (() => {
        if (selectedIds.size === 0) return `All crews (${crews.length})`;
        if (selectedIds.size === crews.length) return `All ${crews.length} crews`;
        if (selectedIds.size === 1) {
            const c = crews.find((x) => selectedIds.has(x.id));
            return c?.name || c?.email || "1 crew";
        }
        return `${selectedIds.size} of ${crews.length} crews`;
    })();

    return (
        <div
            ref={wrapperRef}
            className="relative flex items-center gap-2 flex-wrap"
            data-testid="crew-filter"
        >
            <span className="text-xs text-muted font-display font-bold uppercase tracking-wider mr-1">
                Crews:
            </span>

            {/* Inline search input — always visible for fast typing */}
            <div
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md"
                style={{
                    background: "var(--brand-white, #fff)",
                    border: "1px solid rgba(0,0,0,0.12)",
                    minWidth: 200,
                }}
            >
                <Search size={14} className="opacity-60" />
                <input
                    type="text"
                    placeholder="Filter crews..."
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        if (!open) setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    className="bg-transparent outline-none text-sm flex-1 min-w-0"
                    data-testid="crew-filter-search"
                />
                {search && (
                    <button
                        type="button"
                        onClick={() => setSearch("")}
                        aria-label="Clear search"
                        className="opacity-60 hover:opacity-100"
                        data-testid="crew-filter-search-clear"
                    >
                        <X size={13} />
                    </button>
                )}
            </div>

            {/* Multi-select dropdown trigger */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="btn btn-ghost"
                data-testid="crew-filter-toggle"
            >
                <Users size={14} />
                <span className="text-sm">{summary}</span>
                <ChevronDown size={12} />
            </button>

            {/* Active selection chips */}
            {selectedIds.size > 0 && selectedIds.size < crews.length && (
                <button
                    type="button"
                    onClick={clearAll}
                    className="text-xs underline opacity-70 hover:opacity-100"
                    data-testid="crew-filter-clear"
                >
                    Show all
                </button>
            )}

            {/* Dropdown panel */}
            {open && (
                <div
                    className="absolute left-0 z-30 mt-1 rounded-md shadow-lg"
                    style={{
                        top: "100%",
                        minWidth: 280,
                        maxWidth: 360,
                        background: "var(--brand-white, #fff)",
                        border: "1px solid rgba(0,0,0,0.12)",
                    }}
                    data-testid="crew-filter-dropdown"
                >
                    <div
                        className="flex items-center justify-between gap-2 px-3 py-2 border-b text-xs"
                        style={{ borderColor: "rgba(0,0,0,0.08)" }}
                    >
                        <span className="text-muted">
                            {visibleCrews.length} of {crews.length} crew
                            {crews.length === 1 ? "" : "s"}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                className="link-red text-xs"
                                onClick={selectAll}
                                data-testid="crew-filter-select-all"
                            >
                                Select all
                            </button>
                            <span className="opacity-30">·</span>
                            <button
                                type="button"
                                className="link-red text-xs"
                                onClick={clearAll}
                                data-testid="crew-filter-clear-all"
                            >
                                Clear
                            </button>
                        </div>
                    </div>

                    <div
                        className="max-h-72 overflow-y-auto"
                        data-testid="crew-filter-options"
                    >
                        {visibleCrews.length === 0 ? (
                            <p
                                className="text-sm text-muted text-center py-6"
                                data-testid="crew-filter-no-match"
                            >
                                No crews match "{search}"
                            </p>
                        ) : (
                            visibleCrews.map((c) => {
                                const checked = selectedIds.has(c.id);
                                return (
                                    <label
                                        key={c.id}
                                        className="flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-black/5"
                                        data-testid={`crew-filter-option-${c.id}`}
                                    >
                                        <span
                                            className="inline-flex items-center justify-center shrink-0 rounded"
                                            style={{
                                                width: 16,
                                                height: 16,
                                                background: checked
                                                    ? "var(--brand-red)"
                                                    : "var(--brand-white, #fff)",
                                                border: `1px solid ${
                                                    checked
                                                        ? "var(--brand-red)"
                                                        : "rgba(0,0,0,0.25)"
                                                }`,
                                            }}
                                        >
                                            {checked && (
                                                <Check size={11} color="#fff" strokeWidth={3} />
                                            )}
                                        </span>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggle(c.id)}
                                            className="sr-only"
                                        />
                                        <span className="flex-1 truncate">
                                            {c.name || c.email}
                                        </span>
                                        <span className="text-xs text-muted capitalize">
                                            {c.role}
                                        </span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
